define((require) => {
    "use strict";

    const _             = require("underscore");
    const $             = require("jquery");
    const {Logger}      = require("shared/util/Logger");
    const Preconditions = require("shared/util/Preconditions");
    const Window        = require("shared/services/Window");

    const log = new Logger("ViewerProxy");

    /**
     * @private
     * Finds options set on "viewer" for the specified child viewer or window to eventually pass on to the
     * child viewer.
     *
     * @param {ViewerProxy}             viewer The parent viewer proxy; never falsey.
     * @param {ViewerProxy|HMTLElement} iframe The child viewer proxy or iframe to eventually store the child viewer proxy;
     *                                         never falsey.
     *
     * @return {{options:*, parent:ViewerProxy}} The entry storing register options and ViewerProxy parent, if any;
     *                                           can be falsey.
     */
    const options = (viewer, iframe) => {
        const options = viewer._options;
        let entry;
        let i;

        for (i = 0; i < options.length; i++) {
            entry = options[i];
            if (entry.iframe === iframe) {
                options.splice(i, 1);
                return entry;
            }
        }
    };

    /**
     * @class
     * Creates a viewer proxy, representing the viewer to be exposed for the channels app in the content window
     * of the iframe. In the channels.js app, the <tt>Viewer</tt> (proxy) type will invoke these functions
     * when applicable, but embedded Viewers on direct urls (on same origin) may also call each other, e.g.
     * to pause the outer Experiences while an embedded Experience has focus. <p>
     *
     * Viewer proxy interface, exported in the iframe window under <tt>viewer</tt> (also in the Editor in preview mode):
     * <pre>
     *    options:       *,                                                  // options to be used during Viewer initialisation for embedded Viewers/Experiences,
     *                                                                       // like the viewport size.
     *    win:           Function(): Window,                                 // returns the Window the Viewer lives in.
     *    iframe:        Function(): HTMLElement,                            // returns the dom iframe the Viewer lives in.
     *    parent:        ViewerProxy,                                        // the parent Viewer if this Viewer is embedded.
     *    register:      Function(win: Window, options:*),                   // registers options for a child viewer before it is available, to be set as
     *                                                                       // 'childViewer.options' once it becomes available.
     *    channel:       Function(): Channel,                                // returns the Channel when embedded via channels, falsey otherwise.
     *    contentLoaded: Function(data:JSON, type:String),                   // called by the channels app when an publishable (experience or json) has been preloaded or failed (no-arg)!
     *    state:         Function(name:String, value:T, recurse:Boolean): T, // If only <tt>name</tt> is supplied, the state is returned. The name
     *                                                                       // can be "autoPlay", "paused", "stalled", "active", "shown", and "closed".
     *                                                                       // If the <tt>value</tt> is also supplied, said state will be said, potentially
     *                                                                       // triggering cascading recursive changes. The <tt>recursive</tt> argument is
     *                                                                       // for internal use only.
     *    close:         Function(),                                         // Irreversibly closes the viewer, but the channel can
     *                                                                       // insert a new viewer again if restarted. If so, it will
     *                                                                       // be in a different iframe and this proxy is void. Same
     *                                                                       // as calling <tt>viewer.state({name: closed", value: true})</tt>.
     * </pre>
     *
     * @param {Function} state     The state function backing this ViewerProxy; cannot be falsey. Typically a
     *                             reference to ExperienceController.state(..) or GroupController.state(..), but
     *                             also used for ExperiencePreview in the editor.
     * @param {Channel}  [channel] The channel when embedded; can be falsey (will be on direct url or in the editor).
     */
    const ViewerProxy = function(state, channel) {
        /**
         * @private @type {Boolean}
         * True if this ViewerProxy is an intermediate one, false otherwise.
         *
         * @see #root
         */
        this._intermediate = false; // determined differently via channels and direct urls... :/

        /**
         * @type {ViewerProxy}
         * The parent Viewer proxy of this Viewer proxy when embedded. <p>
         *
         * By default, this is the Viewer from the parent Window, but for Lightboxes it will be a different one as
         * Lightboxes live in the top most reachable Window always, but may logically have been created inside an
         * iframe window.
         */
        this.parent = (
            (() => {
                // In Channel mode, the top (parent) window may not store the actual parent viewer, so use
                // the Channel to fetch the parent...
                if (channel) {
                    const parent = channel.embedded();
                    let viewer;

                    if (parent) {
                        viewer = parent.viewer();
                        this._intermediate = viewer.win() !== Window.parent();
                    }
                    return viewer;
                }
                // Direct urls, intermediate handled elsewhere because of different Window structure...
                return Window.viewer("parent"); // default, may be overridden
            })()
        );

        /**
         * @private @type {ViewerProxy[]}
         * Array of all embedded viewer proxies of this viewer proxies.
         */
        this._children = [];   // to handle pausing of embedded experiences
        this._options  = [];   // entry: {iframe: HTMLElement, options: {}, parent: ViewerProxy}
        this._deferred = null; // for channel preloading

        /**
         * Viewer options passed by the parent, if any, automatically during setup of this proxy. <p>
         *
         * The options will be used by this Viewer during initialisation/initial rendering, e.g. use the available
         * size even if rendered on a hidden adjacent Scene, and whether or not initially active.
         */
        this.options = undefined;

        /**
         * Returns the window this Viewer Proxy lives in.
         *
         * @return {Window} The window this Viewer Proxy lives in; will be null if the window is closed.
         */
        this.win = () => {
            const win = Window.self();
            if (win && win.closed) {
                return null;
            }
            return win;
        };

        /**
         * Returns the iframe element of the Viewer.
         *
         * @return {HTMLElement} The iframe element of the Viewer; will be null if this ViewerProxy (i.e. its window)
         *                       is closed.
         */
        this.iframe = () => {
            const win = this.win();
            return win ? win.frameElement : null;
        };

        /**
         * Returns the top most (intermediate) root Viewer from this Viewer, which might be itself. <p>
         *
         * An intermediate root Viewer is when a ViewerProxy lives in a window whose parent window stores a
         * different ViewerProxy that the parent ViewerProxy of the intermediate root, like a Viewer opened
         * in a Lightbox as Lightboxes always live in the global window/dom.
         *
         * @param  {Boolean} [intermediate] True to fetch the closest intermediate root, false to fetch the top
         *                                  most one (default). The closest intermediate root is the one
         *                                  where the ViewerProxy lives in a window whose parent window stores a
         *                                  different ViewerProxy that the parent ViewerProxy of the intermediate
         *                                  root, like a Viewer opened in a Lightbox as Lightboxes always live
         *                                  in the global window/dom. It may be this Viewer if it intermediate.
         * @return {ViewerProxy} The top most reachable Viewer from this Viewer; never null, can be this Viewer.
         */
        this.root = (intermediate) => {
            if (!this.parent || (intermediate && this._intermediate)) {
                return this;
            }
            return this.parent.root(intermediate);
        };

        /**
         * Returns the accessible (distinct) siblings Viewer Proxies of this Viewer Proxy, if any. <p>
         *
         * Any such sibling will share the same parent, or have no parent at all (i.e. all top level
         * Viewers).
         *
         * @param  {Boolean} [removeSelf] True to remove this Viewer Proxy from the array, false to include it (default).
         * @return {ViewerProxy[]} The siblings Viewer Proxies in a new array, if any; never falsey, but can be empty.
         *                         Modifying the returned array will not affect this proxy.
         */
        this.siblings = (removeSelf) => {
            // Parent can also be null in the editor...
            let parent = this.parent;
            let siblings;

            // Non-top level means this Viewer is embedded within another Viewer that already keeps track of its
            // children, which of course are the siblings of this Viewer Proxy...
            if (parent) {
                siblings = _.clone(parent._children);

            // At top level, we have to traverse the dom to find other Viewers not related to this Viewer, e.g.
            // on a page with multiple distinct top level non embedded Experiences/Viewers. Note that we may have
            // a "false positive" on top level in the Editor, where the actual top most Viewer is off limits due
            // to cross domain issue between Editor and (embedded) Viewers. At top level, the document may be
            // the actual document to search on a direct url via as-is, but more likely the document to search is
            // the document embedding the Experiences, either via channels or via iframes with direct urls...
            } else {
                siblings = [];

                // Must have an *accessible* parent window to access the document to search for siblings. If we do not
                // have a parent, we cannot ever find siblings but we might want to return this viewer in the
                // case we are on a direct url not embedded in another document...
                parent = Window.parent(); // not ViewerProxy.parent!
                if (parent) {
                    // The first filter ("data-zmags-viewer") will find any Viewer embedded via Channels, EmbedWidget,
                    // and Lightbox, but cannot find Viewers embedded via a direct url hardcoded in the client dom
                    // (by the client), so we have to test for any iframe src storing "viewer.html#.." as well
                    // (rare case, I suspect) - which is why we first fetch all iframes, then filter... :/
                    const iframes = $("iframe", parent.document).filter("[data-zmags-viewer],[src*='viewer.html']");
                    let i;

                    // We may not have access to the iframe in question, e.g. hardcoded urls or when in the Editor
                    // preview mode, or we could potentially get false positives on "viewer.html" part...
                    for (i = 0; i < iframes.length; i++) {
                        try {
                            siblings.push(Window.viewer(iframes[i]));
                        } catch (e) { // e.g. SecurityError
                            // fall-through
                        }
                    }
                } else {
                    siblings.push(this);
                }
            }

            if (removeSelf) {
                siblings.splice(_.indexOf(siblings, this), 1);
            }

            return siblings;
        };

        /**
         * Registers options supplied as the second argument for the specified Viewer Proxy associated with
         * the (iframe) window supplied as the first argument. Once the Viewer in said window is ready and registers
         * itself with this (parent) Viewer Proxy, the embedded child viewer will have its <tt>options</tt> property
         * set with said options, to be used during its initialisation.
         *
         * @param {HTMLElement}            [iframe]  The child iframe (dom element, not content window as it may not
         *                                           be available yet) where the Viewer Proxy will eventually load;
         *                                           cannot be falsey.
         * @param {{state: *, options: *}} [options] The state and options for the child viewer to use during
         *                                           initialisation.
         * @param {ViewerProxy}            [parent]  The logical parent ViewerProxy, defaulting to the current one.
         *
         * @see   #state
         * @see   #options
         */
        this.register = (...args) => { // note: only doc for public functionality, not for private use
            const i = args.length;
            let parent;

            if (i) {
                const viewer = args[0];
                Preconditions.checkArgument(viewer, "Viewer cannot be falsey");

                const register = i === 1;
                const iframe   = register ? viewer.iframe() : viewer;
                const entry    = options(this, iframe) || {}; // find and remove!
                const opts     = entry.options;

                parent = entry.parent || this;

                // This viewer is called by an embedded child now available...
                if (register) {
                    // Special case: a Lightbox will always use the top most window to ensure the Lightbox overlay
                    // and Lightbox itself is relative to the overall window, and not some (embedded) Experience.
                    // Because of this, the logical parent viewer may not live in the parent window! We update the
                    // logical structure to ensure we have a proper tree structure, for example to test the depth
                    // of embedding...
                    if (viewer.parent !== parent) {
                        viewer.parent = parent;
                        viewer._intermediate = true;
                    }
                    parent._children.push(viewer);

                    // Find options for the child viewer, if any...
                    if (opts) {
                        viewer.options = opts;
                        log.verbose(`Setting embedded Viewer options: ${viewer}`, opts);

                        // Set state on the *child* viewer, if specified...
                        if (opts.state) {
                            // Internally, the viewer might not be ready yet, e.g. on delegate controller usage,
                            // but eventually, the state will be used by the ExperienceViewer in question otherwise...
                            viewer.state(opts.state);
                        }
                    }

                // This Viewer is a parent storing options for a given child/embedded Viewer that is not available
                // yet, only the iframe window in question. Duplicate options will already have been removed above.
                } else {
                    this._options.push({iframe, options: args[1], parent: args[2]}); // "parent" can be undefined, will default
                }

            // A viewer proxy registering itself to the current window *and* at its parent, if any. The parent will be
            // null for the top most reachable Viewer...
            } else {
                // Export to "viewer", which will never conflict as we always have a single Viewer inside a single iframe!
                // On "close()", will be removed again...
                window.viewer = this;

                // At this point, the parent Viewer is *always* the one in the parent window on direct urls, if any,
                // but it might not be the logical parent Viewer for Lightboxes; the parent register call will ensure
                // proper structure henceforth. In Channel mode, the parent Viewer is acquired from the Channel and
                // thus logically correct regardless of Window at this point...
                if (this.parent) {
                    this.parent.register(this);
                }
            }
        };

        /**
         * @protected
         * Unregisters this Viewer proxy from its possible parent and clears the "viewer" reference for the associated
         * window. Any unused options for this viewer proxy will also be removed by this.
         */
        this._unregister = (...args) => {
            // Called from child that wants to unregister itself from its parent
            if (args.length) {
                const viewer = args[0];
                let i = this._children.length;

                while (i--) {
                    if (this._children[i] === viewer) {
                        this._children.splice(i, 1);

                        // Remove any unused options...
                        options(this, viewer.iframe());
                    }
                }

            // Called by this viewer, e.g. on close...
            } else {
                // Unregister from parent, if any...
                if (this.parent) {
                    this.parent._unregister(this);
                }
                // Unregister from window...
                delete window.viewer;
            }
        };

        /**
         * Returns the channel available to this Viewer Proxy, if any. <p>
         *
         * Will be null when the Experience/Group is viewed on a direct url (or in the Editor).
         *
         * @return {Channel} The channel, if any; can be falsey.
         *
         * @see    #contentLoaded
         */
        this.channel = () => channel;

        /**
         * Channel callback when channel preloading is enabled and the preload has finished, notifying this viewer
         * about this. <p>
         *
         * Only relevant (called) when this Viewer Proxy is backend by a channel.
         *
         * @param {*}       data  The json data preloaded, e.g. an Experience; a falsey value means failure to load.
         * @param {String} [type] The type of json data preloaded, e.g. "experience" or "group".
         *
         * @see   ViewerBridge#contentLoaded
         */
        this.contentLoaded = (data, type) => { // called via channel.viewer().contentLoaded
            if (this._deferred) {
                if (data) {
                    log.verbose("Content preloaded: ", type, data);
                    this._deferred.resolve(data);
                } else {
                    log.error("Content preload failed: ", type);
                    this._deferred.reject();
                }
                this._deferred = null;
            }
        };

        /**
         * Gets or sets the state for this Viewer Proxy. <p>
         *
         * If a single string argument is supplied, the value of said viewer state attribute is returned. The value
         * can then be either "autoPlay", "paused", "stalled", "active", "shown", or "closed" (all Booleans). <p>
         *
         * If options like {name:String, value:Boolean, recursive:Boolean} are specified, where the <tt>name</tt>
         * attribute can be either of the aforementioned attributes with an associated Boolean <tt>value</tt>,
         * e.g. {name: "stalled", value: true, ..}, the specified option will be set. If the <tt>recursive</tt>
         * option is not set, it will default to true. <p>
         *
         * Depending on the type of state to set, this will recursively recurse into in embedded child viewers if
         * the <tt>recursive</tt> option is (defaulted) to true, setting the same state (if not already) on embedded
         * child viewers. For "autoPlay", recursion will always be disabled, and it cannot be re-enabled for
         * a given Experience if already disabled. <p>
         *
         * Setting the "closed" state to true will effectively close (and destroy) this Viewer Proxy (and Viewer) for
         * good, including any recursively embedded child Viewers.
         *
         * @param {*} options If a string, the name of the state property to return: "paused", "stalled", "active",
         *                    "shown", or "closed". If options, specified like {name:String, value:Boolean, recursive:Boolean},
         *                    where <tt>name</tt> is one the four aforementioned names,
         *                    e.g. {name: "active", value:false, recursive:true}.
         *
         * @return {Boolean} The state value if only a state attribute is supplied as argument, e.g. "stalled";
         *                   otherwise undefined.
         */
        this.state = (options) => {
            if (typeof options === "string") {
                return state ? state(options) : true; // "get", no state means closed, stalled, and paused!
            }

            const name = options.name;
            let recursive = (typeof options.recursive === "undefined" || options.recursive);
            let i;

            // Special case: auto play is for current Experience only, i.e. on direct user interaction...
            if (name === "autoPlay") {
                recursive = false;
            }

            // The controller ExperienceViewModel will ensure we recursively call our parent when needed, like
            // for "paused",, i.e. the Viewer potentially embedding this Viewer, so we only need to handle
            // siblings at this point. We do not close here, we need to recurse into children and close them
            // first...
            if (name !== "closed") {
                state(name, options.value, recursive);
            }

            // On certain states, like "stalled", we need to visit all siblings as well, so we handle this
            // explicitly here as we know we will initially be triggered from the top most reachable Viewer...
            if (recursive) {
                for (i = 0; i < this._children.length; i++) {
                    this._children[i].state(options); // manually recurse!
                }
            }

            // Call ViewerProxy._close to clean all state...
            if (name === "closed") {
                this._close();
            }
        };

        /**
         * Closes this Viewer Proxy, and thus the Viewer itself. <p>
         *
         * This is syntactic sugar for calling <tt>self.state({name: "closed", value: true})</tt>; it will thus
         * recursively close any embedded Viewers as well.
         */
        this.close = () => {
            this.state({name: "closed", value: true}); // ensures recursion!
        };

        // Closes *this* viewer...
        this._close = () => {
            if (state) {
                log.verbose(`Closing viewer: ${this}`);

                state("closed", true); // will typically destroy the main region, thus the viewer
                state = null;

                // If embedded via channels, notify channel so it can clean up, e.g. timers...
                if (channel) {
                    channel.callbacks.closed();
                    channel = null;
                }

                // Unregister this viewer, including from its potential parent...
                this._unregister();
            }
        };

        this.toString = () => {
            let s = "ViewerProxy[";
            const win = this.win();

            // A closed window where we still have a reference to this ViewerProxy is a severed (programmer) error,
            // so we log it like this to be able to spot such invalid states...
            if (!win) {
                s += "win: closed; ";
            }
            if (state) { // i.e. not closed?
                s += "type: ";
                if (this.parent) {
                    if (this._intermediate) {
                        s += "intermediate-";
                    }
                    s += this._children.length ? "node" : "leaf";
                } else {
                    s += "root";
                }
                s += `; active: ${state("active")}; stalled: ${state("stalled")}; paused: ${state("paused")}`;
                if (channel) {
                    s += `; channel: ${channel}`;
                }
            } else {
                s += "closed";
            }
            return `${s}]`;
        };

        // Register to its parent for communication, like pausing embedded Experiences, getting back potential options
        // for this Viewer set by the parent, like the initial embedding size...
        this.register();
    };

    return ViewerProxy;
});



// WEBPACK FOOTER //
// ./src/shared/ui/viewer/ViewerProxy.js